import type { ExperimentUser } from '@amplitude/experiment-js-client';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { Country } from '../types/country';
import { getCurrentCountry } from '../utils/country';

export type LoginExperimentUserParams = {
  userId: string;
  countryCode: Country;
  userProperties?: ExperimentUser['user_properties'];
};

interface ExperimentContextParams {
  experimentClient?: ExperimentClient;
  initialVariants?: Variants;
  loginExperimentUser: (params: LoginExperimentUserParams) => Promise<void>;
  logoutExperimentUser: () => Promise<void>;
  refetch: () => Promise<void>;
  isReady: boolean;
}

export const ExperimentContext = createContext<ExperimentContextParams | undefined>(undefined);

interface ExperimentProviderProps {
  experimentClient: ExperimentClient;
  deviceId: string;
  variantPollingInterval?: number;
  /**
   * Variants already evaluated for this `deviceId` before the app mounted, used by `useVariant`
   * until the client fetch resolves. Only a server-rendered app can supply these; every other
   * consumer omits it and keeps returning `undefined` during that window.
   *
   * They must be the exact values the server rendered with, not a fresh read: the first client
   * render has to produce the same markup as the HTML it hydrates.
   */
  initialVariants?: Variants;
}

// Abstract type for Variant. Unfortunately Amplitude do not have a "core" typing strategy to import from yet
// They have two (similar) definitions for an Variant:
// - node_modules/@amplitude/experiment-react-native-client/lib/typescript/types/variant.d.ts
// - node_modules/@amplitude/experiment-js-client/dist/types/src/types/variant.d.ts
export type Variant = {
  key?: string;
  value?: string;
  payload?: any;
  expKey?: string;
  metadata?: Record<string, unknown>;
};

export type Variants = Record<string, Variant>;

// Abstract type for ExperimentClient. Unfortunately Amplitude do not have a "core" typing strategy to import from yet
// They have two definitions for an ExperimentClient:
// - node_modules/@amplitude/experiment-react-native-client/lib/typescript/types/client.d.ts
// - node_modules/@amplitude/experiment-js-client/dist/types/src/types/client.d.ts
export type ExperimentClient = {
  start(): void;
  clear(): void;
  setUser(user: Record<string, any>): void;
  fetch(user?: ExperimentUser): Promise<ExperimentClient>;
  variant(variant: string): Variant;
};

export const ExperimentProvider = ({
  experimentClient,
  deviceId,
  variantPollingInterval,
  initialVariants,
  children,
}: PropsWithChildren<ExperimentProviderProps>) => {
  const [isReady, setIsReady] = useState(false);
  const variantPolling = useRef<ReturnType<typeof setInterval> | null>(null);
  const experimentRef = useRef<
    | {
        userId: string | undefined;
        countryCode: Country | undefined;
        userProperties: ExperimentUser['user_properties'];
      }
    | undefined
  >(null);

  const signedOutUser = useMemo(
    () => ({
      device_id: deviceId,
      user_properties: {
        country_code: getCurrentCountry(),
      },
    }),
    [deviceId],
  );

  const refetch = useCallback(async () => {
    await experimentClient.fetch({
      ...signedOutUser,
      user_id: experimentRef.current?.userId,
      user_properties: {
        ...signedOutUser.user_properties,
        ...(experimentRef.current?.countryCode ? { country_code: experimentRef.current?.countryCode } : {}),
        ...experimentRef.current?.userProperties,
      },
    });

    setIsReady(true);
  }, [experimentClient, signedOutUser]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reason
  useEffect(() => {
    refetch();
  }, []);

  const startExperiment = useCallback(() => {
    experimentClient.start();

    if (variantPollingInterval && !variantPolling.current) {
      variantPolling.current = setInterval(refetch, variantPollingInterval);
    }
  }, [experimentClient, refetch, variantPollingInterval]);

  const stopExperiment = useCallback(() => {
    experimentClient.clear();

    if (variantPolling.current) {
      clearInterval(variantPolling.current);
    }
  }, [experimentClient]);

  useEffect(() => {
    startExperiment();
    return stopExperiment;
  }, [startExperiment, stopExperiment]);

  const loginExperimentUser = useCallback(
    async ({ userId, countryCode, userProperties = {} }: LoginExperimentUserParams) => {
      experimentRef.current = { userId, countryCode, userProperties };
      await refetch();
    },
    [refetch],
  );

  const logoutExperimentUser = useCallback(async () => {
    experimentClient.clear();
    experimentRef.current = undefined;
    await refetch();
  }, [experimentClient, refetch]);

  const experimentContext = useMemo(
    () => ({ experimentClient, initialVariants, loginExperimentUser, logoutExperimentUser, refetch, isReady }),
    [experimentClient, initialVariants, loginExperimentUser, logoutExperimentUser, refetch, isReady],
  );

  return <ExperimentContext.Provider value={experimentContext}>{children}</ExperimentContext.Provider>;
};

const useContextOrThrow = (errorMessage: string) => {
  const context = useContext(ExperimentContext);
  if (!context?.experimentClient) {
    throw new Error(errorMessage);
  }
  return context;
};

export const useExperiment = () => useContextOrThrow('useExperiment must be used within an ExperimentProvider');

export const useVariant = (variant: string) => {
  const { experimentClient, isReady, initialVariants } = useContextOrThrow(
    'useVariant must be used within an ExperimentProvider',
  );

  // Before the fetch resolves the client holds nothing, so reading it would return a fallback
  // rather than the assignment the server already rendered.
  return isReady ? experimentClient!.variant(variant) : initialVariants?.[variant];
};

export const useBooleanVariant = (variant: string) => {
  const { experimentClient, isReady, initialVariants } = useContextOrThrow(
    'useBooleanVariant must be used within an ExperimentProvider',
  );

  return (isReady ? experimentClient!.variant(variant) : initialVariants?.[variant])?.value === 'on';
};

type VariantGateProps = PropsWithChildren<{}> & {
  variant: Required<Variant>['key'];
  valueToShow: Required<Variant>['value'];
};

export const VariantGate = ({ variant, valueToShow, children }: VariantGateProps) => {
  const variantObject = useVariant(variant);
  const shouldRender = variantObject && variantObject.value === valueToShow;

  if (shouldRender) return children;

  return null;
};
